// filepath: client.service.ts
// Auth headers provided globally by ApiHeadersInterceptor — no token needed here.
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  Client,
  ClientPageResponse,
  ClientSingleResponse,
  NIABioData,
  NIAVerificationResponse,
  NIAPersonRaw
} from './client.model';
import { EnvService } from '../service/env/env.service';

@Injectable({
  providedIn: 'root'
})
export class ClientService {

  private baseUrl: string;

  constructor(private http: HttpClient, private env: EnvService) {
    this.baseUrl = this.env.apiUrl + '/client';
  }

  // ══════════════════════════════════════════════════════════
  // CRUD — Real API
  // ══════════════════════════════════════════════════════════

  /**
   * GET /client?page=&size=&status=
   * Fetches a paginated list of clients.
   */
  getAllClients(page: number = 0, size: number = 10, search?: string, status?: string): Observable<ClientPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (search && search.trim()) { params = params.set('search', search.trim()); }
    if (status && status.trim()) { params = params.set('status', status.trim()); }

    return this.http.get<ClientPageResponse>(this.baseUrl, { params });
  }

  /**
   * GET /client?page=0&size=1&clientCode=|phone=|email=
   * Search for a single client by a unique identifier.
   */
  searchClientBy(searchBy: 'clientCode' | 'phone' | 'email' | 'ghCardNumber', value: string): Observable<ClientPageResponse> {
    const params = new HttpParams()
      .set('page', '0')
      .set('size', '1')
      .set(searchBy, value.trim());

    return this.http.get<ClientPageResponse>(this.baseUrl, { params });
  }

  /**
   * GET /client/:id
   */
  getClientById(id: number): Observable<ClientSingleResponse> {
    return this.http.get<ClientSingleResponse>(this.baseUrl + '/' + id);
  }

  /**
   * POST /client
   */
  createClient(client: Client): Observable<ClientSingleResponse> {
    const payload = {
      surname: client.surname,
      otherNames: client.otherNames,
      fullName: client.otherNames + ' ' + client.surname,
      dateOfBirth: client.dateOfBirth,
      gender: client.gender || '',
      nationality: client.nationality || '',
      occupation: client.occupation,
      telephone: client.telephone,
      email: client.email,
      preferredContact: client.preferredContact,
      ghCardNumber: client.ghCardNumber,
      postalAddress: client.postalAddress,
      digitalAddress: client.digitalAddress,
      cellNumber: client.cellNumber,
      status: client.status || 'Active',
      verificationStatus: client.verificationStatus || 'Verified'
    };
    return this.http.post<ClientSingleResponse>(this.baseUrl, payload);
  }

  /**
   * PUT /client/:id
   */
  updateClient(id: number, changes: Partial<Client>): Observable<ClientSingleResponse> {
    const payload: any = {
      email: changes.email,
      telephone: changes.telephone,
      fullName: (changes.otherNames && changes.surname)
        ? changes.otherNames + ' ' + changes.surname
        : undefined
    };

    if (changes.surname !== undefined)           { payload.surname = changes.surname; }
    if (changes.otherNames !== undefined)         { payload.otherNames = changes.otherNames; }
    if (changes.dateOfBirth !== undefined)        { payload.dateOfBirth = changes.dateOfBirth; }
    if (changes.gender !== undefined)             { payload.gender = changes.gender; }
    if (changes.nationality !== undefined)        { payload.nationality = changes.nationality; }
    if (changes.occupation !== undefined)         { payload.occupation = changes.occupation; }
    if (changes.preferredContact !== undefined)   { payload.preferredContact = changes.preferredContact; }
    if (changes.ghCardNumber !== undefined)       { payload.ghCardNumber = changes.ghCardNumber; }
    if (changes.postalAddress !== undefined)      { payload.postalAddress = changes.postalAddress; }
    if (changes.digitalAddress !== undefined)     { payload.digitalAddress = changes.digitalAddress; }
    if (changes.cellNumber !== undefined)         { payload.cellNumber = changes.cellNumber; }
    if (changes.status !== undefined)             { payload.status = changes.status; }
    if (changes.verificationStatus !== undefined) { payload.verificationStatus = changes.verificationStatus; }

    return this.http.put<ClientSingleResponse>(this.baseUrl + '/' + id, payload);
  }

  /**
   * DELETE /client/:id
   */
  deleteClient(id: number): Observable<ClientSingleResponse> {
    return this.http.delete<ClientSingleResponse>(this.baseUrl + '/' + id);
  }

  // ══════════════════════════════════════════════════════════
  // NIA Verification (Verifast API)
  // ══════════════════════════════════════════════════════════

  verifyWithNIA(ghCardNumber: string, selfieBase64: string): Observable<{ success: boolean; bioData?: NIABioData; errorMessage?: string }> {
    var NIA_API_URL = 'https://api.verifast360.com/api/v1/customer/verification';
    var NIA_TOKEN   = 'verifast_e889e400.HTGXjpULzrQ5RPSfVapleMPWh2Cb5vNN';

    // Convert base64 data-URL to a Blob for multipart/form-data
    var blob = this.base64ToBlob(selfieBase64);
    var extension = this.getExtensionFromBase64(selfieBase64);

    var formData = new FormData();
    formData.append('image', blob, 'selfie.' + extension);
    formData.append('pinNumber', ghCardNumber.trim());
    formData.append('dataType', 'PNG');
    formData.append('transactionDescription', 'Client onboarding verification');
    formData.append('center', 'BRANCHLESS');

    var headers = new HttpHeaders({
      'Authorization': 'x-api-key ' + NIA_TOKEN
      // NOTE: Do NOT set Content-Type here — the browser must set it
      // automatically with the correct multipart boundary for FormData.
    });

    return this.http.post<NIAVerificationResponse>(NIA_API_URL, formData, { headers: headers }).pipe(
      map((response) => {
        if (response.responseCode === '000' && response.data && response.data.data) {
          var apiData = response.data.data;

          if (apiData.verified !== 'TRUE') {
            return { success: false, errorMessage: 'Identity could not be verified. Please retake your selfie and try again.' };
          }

          var person = apiData.person;
          var bioData = this.mapPersonToBioData(person, ghCardNumber);
          return { success: true, bioData: bioData };
        }

        // Non-000 response code
        var msg = response.responseMessage || 'Verification failed. Please try again.';
        return { success: false, errorMessage: msg };
      }),
      catchError((err) => {
        console.error('[NIA] Verification error:', err);
        var serverMsg = err.error && err.error.responseMessage
          ? err.error.responseMessage
          : 'Verification service unavailable. Please try again later.';
        return of({ success: false, errorMessage: serverMsg });
      })
    );
  }

  // ══════════════════════════════════════════════════════════
  // Private Helpers
  // ══════════════════════════════════════════════════════════

  /** Map the Verifast API person object to our NIABioData model */
  private mapPersonToBioData(person: any, ghCardNumber: string): NIABioData {
    var residenceAddr = (person.addresses || []).find(function(a: any) { return a.type === 'Residence'; })
      || (person.addresses || [])[0]
      || {};

    var phoneNumbers = (person.contact && person.contact.phoneNumbers) || [];
    var mobile = phoneNumbers.find(function(p: any) { return p.type === 'Mobile'; }) || phoneNumbers[0] || {};

    return {
      surname: (person.surname || '').trim(),
      otherNames: (person.forenames || '').trim(),
      dateOfBirth: person.birthDate || '',
      gender: person.gender || '',
      nationality: person.nationality || '',
      ghCardNumber: person.nationalId || ghCardNumber,
      occupation: (person.occupations && person.occupations.length > 0)
        ? person.occupations[0]
        : '',
      telephone: mobile.phoneNumber || '',
      email: person.contact.email || '',
      postalAddress: [residenceAddr.community, residenceAddr.town, residenceAddr.districtName, residenceAddr.region]
        .filter(Boolean).join(', '),
      digitalAddress: residenceAddr.addressDigital || '',
      placeOfBirth: [person.birthTown, person.birthDistrict, person.birthRegion]
        .filter(Boolean).join(', '),
      cardValidFrom: person.cardValidFrom || '',
      cardValidTo: person.cardValidTo || '',
      photoBase64: (person.biometricFeed && person.biometricFeed.face)
        ? person.biometricFeed.face.data || ''
        : ''
    };
  }

  /** Convert a base64 data-URL (e.g. "data:image/jpeg;base64,...") to a Blob */
  private base64ToBlob(dataUrl: string): Blob {
    var parts = dataUrl.split(',');
    var mimeMatch = parts[0].match(/:(.*?);/);
    var mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    var binary = atob(parts[1]);
    var array = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  }

  /** Extract file extension from a base64 data-URL */
  private getExtensionFromBase64(dataUrl: string): string {
    var match = dataUrl.match(/data:image\/(\w+);/);
    if (!match) { return 'jpeg'; }
    var ext = match[1].toLowerCase();
    return ext === 'jpg' ? 'jpeg' : ext;
  }
}
